<i18n src="./resources/locales.json"></i18n>
<template>
  <div class="table-container">
    <div>
      <EcsTable
        :key="componentKey"
        :headers="displayedHeaders"
        :items="summaryData"
        :options="options"
        :filter-options="filterOptions"
        :custom-sort="customSort"
        :custom-filter="customFilter"
        :search="filterValue"
        :id-field="'businessProcessId'"
        :sorted-business-processes-ids="sortedBusinessProcessesIds"
        filterable
        @optionsUpdate="onOptionsUpdate"
        @sortedIdsChanged="onSortedIdsChanged"
        @currentItems="onItemsChanged"
      >
        <template #top>
          <div class="d-flex flex-row ma-2 align-center">
            <v-row>
              <v-col>
                <v-select
                  v-model="filterColumn"
                  clearable
                  :items="filterableHeaders"
                  :label="$t('filter.column')"
                ></v-select>
              </v-col>
              <v-col>
                <v-select v-model="filterOperator" :items="operators" :label="$t('filter.operator')"></v-select>
              </v-col>
              <v-col>
                <v-text-field v-model="filterValue" clearable :label="$t('filter.value')"></v-text-field>
              </v-col>
            </v-row>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" color="primary" fab x-small v-on="on" @click="showHideColumns">
                  <v-icon>
                    mdi-cogs
                  </v-icon>
                </v-btn>
              </template>
              <span v-once>{{ $t('settings') }}</span>
            </v-tooltip>
          </div>
        </template>
      </EcsTable>
      <EcsHideColumns
        :items="supervisorSummaryTable"
        :display="displayHideColumns"
        :dialog-close="onDialogClose"
        :dialog-accept="onDialogAccept"
        :business-processes="businessProcesses"
        :hidden-business-processes-ids="hiddenBusinessProcessesIds"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import EcsTable from '@/components/ecs-table/EcsTable';
import EcsHideColumns from '@/components/ecs-business-process-summary-table/EcsHideColumns';
import { COLUMN_TYPE } from '@/constants/table';
import { defaultSupervisorSummaryTable } from '@/store/modules/settings';

export default {
  name: 'EcsBusinessProcessSummaryTable',
  components: {
    EcsTable,
    EcsHideColumns
  },
  data() {
    return {
      componentKey: 1,
      displayHideColumns: false,
      filterColumn: null,
      filterOperator: null,
      filterValue: null,
      options: {
        page: 1,
        sortBy: [''],
        sortDesc: [false]
      },
      filterOptions: {
        filterCriteria: '',
        operator: '',
        filterValue: ''
      },
      filterOperators: [
        {
          text: this.$t('operators.lessThan'),
          value: '<',
          type: [COLUMN_TYPE.NUMBER, COLUMN_TYPE.PERCENTAGE]
        },
        {
          text: this.$t('operators.greaterThan'),
          value: '>',
          type: [COLUMN_TYPE.NUMBER, COLUMN_TYPE.PERCENTAGE]
        },
        {
          text: this.$t('operators.equalTo'),
          value: '===',
          type: [COLUMN_TYPE.NUMBER, COLUMN_TYPE.PERCENTAGE, COLUMN_TYPE.STRING]
        },
        {
          text: this.$t('operators.contains'),
          value: 'CONTAINS',
          type: [COLUMN_TYPE.STRING]
        }
      ]
    };
  },
  computed: {
    ...mapGetters('SETTINGS', ['supervisorSummaryTable', 'supervisorSummaryTableSettings']),
    ...mapGetters('STATS', ['getBusinessProcessesStats']),
    summaryData() {
      let formattedObject = this.getBusinessProcessesStats
        .filter(bpStat => !this.hiddenBusinessProcessesIds.includes(bpStat.businessProcessId))
        .map(bpStat => {
          const obj = { businessProcessName: bpStat.businessProcessName, businessProcessId: bpStat.businessProcessId };
          this.supervisorSummaryTable.forEach(header => {
            if (header.value !== 'businessProcessName') {
              obj[header.value] = bpStat[header.value].value;
            }
          });
          return obj;
        });
      let itemsSorted = [];
      this.sortedBusinessProcessesIds.forEach(bpId => {
        const currentItem = formattedObject.find(bpItem => bpItem.businessProcessId === bpId);
        if (currentItem) {
          itemsSorted.push(currentItem);
          formattedObject = formattedObject.filter(
            bpItem => bpItem.businessProcessId !== currentItem.businessProcessId
          );
        }
      });
      itemsSorted = [...itemsSorted, ...formattedObject];
      const summaryRow = this.calculateTotalsData(itemsSorted, this.$t('summary'));
      return [summaryRow, ...itemsSorted];
    },
    hiddenBusinessProcessesIds() {
      return this.supervisorSummaryTableSettings.hiddenBusinessProcessesIds;
    },
    sortedBusinessProcessesIds() {
      return this.supervisorSummaryTableSettings.sortedBusinessProcessesIds;
    },
    businessProcesses() {
      return this.getBusinessProcessesStats.map(({ businessProcessId, businessProcessName }) => ({
        businessProcessId,
        businessProcessName
      }));
    },
    filterableHeaders() {
      return this.supervisorSummaryTable
        .filter(header => header.filterable)
        .map(header => {
          return {
            ...header,
            text: this.$t(header.text)
          };
        });
    },
    operators() {
      if (!this.filterColumn) {
        return [];
      }
      const header = this.supervisorSummaryTable.find(header => header.value === this.filterColumn);
      return this.filterOperators.filter(operator => operator.type.includes(header.type));
    },
    displayedHeaders() {
      return this.supervisorSummaryTable
        .filter(header => !header.hide)
        .sort((a, b) => a.columnIndex - b.columnIndex)
        .map(header => {
          return { ...header, text: this.$t(header.text) };
        });
    }
  },
  watch: {
    filterValue(newValue, oldValue) {
      if (!newValue && oldValue && this.filterColumn && this.filterOperator) {
        this.forceUpdate();
      }
    },
    filterColumn(newValue) {
      if (!newValue) {
        this.forceUpdate();
      }
    }
  },
  mounted() {
    if (this.supervisorSummaryTable.length === 0) {
      this.updateSupervisorSummaryTable(defaultSupervisorSummaryTable);
    }
  },
  methods: {
    ...mapActions('SETTINGS', ['updateSupervisorSummaryTable', 'updateSupervisorSummaryTableSettings']),
    onOptionsUpdate(newOptions) {
      this.options = { ...newOptions };
    },
    formatTable(items, sortBy, isDesc) {
      return items.sort((a, b) => this.tableSort(a, b, sortBy, isDesc));
    },
    customSort(items, index, isDesc) {
      if (!items || !index) {
        return items;
      }
      items.sort((a, b) => {
        if (!a.businessProcessId) {
          return -1;
        }
        if (!b.businessProcessId) {
          return 1;
        }
        const typeOfValue = typeof a[index];
        if (typeOfValue === 'string') {
          return this.sortString(a, b, index, isDesc[0]);
        } else if (typeOfValue === 'number') {
          return this.sortNumber(a, b, index, isDesc[0]);
        } else {
          return null;
        }
      });
      return items;
    },
    sortString(a, b, index, isDesc) {
      const str1 = a[index];
      const str2 = b[index];
      if (isDesc) {
        return str1.localeCompare(str2);
      } else {
        return str2.localeCompare(str1);
      }
    },
    sortNumber(a, b, index, isDesc) {
      if (isDesc) {
        return b[index] - a[index];
      } else {
        return a[index] - b[index];
      }
    },
    customFilter(value, search, item) {
      if (!item.businessProcessId) {
        return true;
      }
      if (!this.filterColumn || !this.filterOperator) {
        return true;
      }
      const valueToFilter = item[this.filterColumn];
      return this.applyOperator(search, valueToFilter, this.filterOperator);
    },
    applyOperator(filter, valueToFilter, filterOperator) {
      const header = this.supervisorSummaryTable.find(header => header.value === this.filterColumn);
      const headerType = header.type;
      if (headerType === COLUMN_TYPE.PERCENTAGE) {
        valueToFilter = valueToFilter * 100;
        filter = Number(filter);
      }
      if (headerType === COLUMN_TYPE.NUMBER) {
        filter = Number(filter);
      }
      switch (filterOperator) {
        case '<':
          return valueToFilter < filter;
        case '>':
          return valueToFilter > filter;
        case '===':
          return valueToFilter === filter;
        case 'CONTAINS':
          if (typeof valueToFilter === 'string' && typeof filter === 'string') {
            return valueToFilter.toUpperCase().includes(filter.toUpperCase());
          } else {
            return valueToFilter.includes(filter);
          }
        default:
          return true;
      }
    },
    showHideColumns() {
      this.displayHideColumns = true;
    },
    onDialogClose() {
      this.displayHideColumns = false;
    },
    onDialogAccept(headers, hiddenBusinessProcessesIds) {
      this.displayHideColumns = false;
      this.updateSupervisorSummaryTableSettings({
        hiddenBusinessProcessesIds,
        sortedBusinessProcessesIds: this.sortedBusinessProcessesIds
      });
      this.updateSupervisorSummaryTable(headers);
    },
    onSortedIdsChanged(newSortedBusinessProcessesIds) {
      this.forceUpdate();
      this.updateSupervisorSummaryTableSettings({
        hiddenBusinessProcessesIds: this.hiddenBusinessProcessesIds,
        sortedBusinessProcessesIds: newSortedBusinessProcessesIds
      });
    },
    forceUpdate() {
      this.componentKey += 1;
    },
    onItemsChanged: debounce(function({ items: filteredItems, tableRef }) {
      if (this.filterValue && this.filterColumn && this.filterOperator && filteredItems && filteredItems.length > 0) {
        const filteredAndValidItems = filteredItems.filter(item => !!item.businessProcessId);
        const cellKeysToUpdate = [
          'businessProcessName',
          'businessProcessAbandonedInteractions',
          'businessProcessAnsweredInteractions',
          'businessProcessOverflowInteractions',
          'businessProcessTotalQueuedInteractions',
          'callbackProcessedInShift',
          'callbackRequestedInShift',
          'callbackToBeProcessed'
        ];
        const tableEl = tableRef.$el;
        const totalsRow = this.calculateTotalsData(filteredAndValidItems, `${this.$t('summary')} *`);
        cellKeysToUpdate.forEach(key => {
          const cellEl = tableEl.querySelector(`[header-name='${key}']`);
          if (cellEl) {
            cellEl.textContent = totalsRow[key];
          }
        });
      }
    }, 250),
    calculateTotalsData(items, totalsLabel = '') {
      const keysToGetTotal = [
        'businessProcessAbandonedInteractions',
        'businessProcessAnsweredInteractions',
        'businessProcessOverflowInteractions',
        'businessProcessTotalQueuedInteractions',
        'callbackProcessedInShift',
        'callbackRequestedInShift',
        'callbackToBeProcessed'
      ];
      return items.reduce(
        (summaryRow, item) => {
          keysToGetTotal.forEach(key => {
            if (!item[key]) {
              return;
            }
            summaryRow[key] = summaryRow[key] + item[key];
          });
          return summaryRow;
        },
        {
          businessProcessName: totalsLabel,
          businessProcessId: '',
          businessProcessASA: '',
          businessProcessAbandonedInteractions: 0,
          businessProcessAbandonedPercentage: '',
          businessProcessAnsweredInteractions: 0,
          businessProcessAnsweredPercentage: '',
          businessProcessOverflowInteractions: 0,
          businessProcessServiceLevel: '',
          businessProcessTotalQueuedInteractions: 0,
          callbackProcessedInShift: 0,
          callbackRequestedInShift: 0,
          callbackToBeProcessed: 0
        }
      );
    }
  }
};
</script>

<style scoped>
.table-container {
  margin: 12px;
  width: 100%;
  display: flex;
  overflow: scroll;
}
</style>
